import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import CreateHealthieAccountChannel from '@/channels/CreateHealthieAccountChannel';
import emptySelectOption from '@/constants/emptySelectOption';
import AppContext from '@/contexts/appContext';
import FieldError from '@/forms/FieldError';
import useOrganizations from '@/hooks/useOrganizations';
import PersonContext from '@/pages/Person/PersonContext';
import CreateHealthieAccountButton from '@/pages/Profile/components/OutlinksSection/components/CreateHealthieAccountButton';
import ReleaseOfInformationRepository from '@/repositories/ReleaseOfInformationRepository';
import { CreateROIPayload, ReleaseOfInformationRecipientType } from '@/types/releaseOfInformation';
import Alert from '@/ui/components/Alert';
import AutocompleteSingleSelect from '@/ui/components/AutocompleteSingleSelect';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import InputGroup from '@/ui/components/Form/InputGroup';
import RadioGroup from '@/ui/components/Form/RadioGroup';
import { OptionType } from '@/ui/components/Form/Select/types';
import Heading from '@/ui/components/Heading';
import Spinner from '@/ui/components/Spinner';
import formatOrganizationToSelectOption from '@/utils/formatOrganizationToSelectOption';
import formatPersonRelationshipToSelectOption from '@/utils/formatPersonRelationshipToSelectOption';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import { defaultRoiFormValues, roiFormSchema } from './roiFormSchema';
import { ROIFormProps, ROIFormValues } from './types';

const ROI_FOR_OPTIONS: OptionType<ReleaseOfInformationRecipientType>[] = [
  {
    label: 'Organization',
    value: ReleaseOfInformationRecipientType.ORGANIZATION
  },
  {
    label: 'Person',
    value: ReleaseOfInformationRecipientType.PERSON
  }
];

export const orgDefaultSearchParams = {
  page: '1',
  search: ''
};

const ROIForm: React.FC<ROIFormProps> = ({ onSuccess }: ROIFormProps) => {
  const { addMessage } = useContext(AppContext);
  const [organizationOptions, setOrganizationOptions] = useState<OptionType<string>[]>([]);
  const [personOptions, setPersonOptions] = useState<OptionType<string>[]>([]);

  const releaseOfInformationRepository = ReleaseOfInformationRepository();

  const { person } = useContext(PersonContext);
  const { person_relationships } = person;

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    watch,
    getValues,
    resetField
  } = useForm<ROIFormValues>({
    defaultValues: defaultRoiFormValues,
    resolver: yupResolver(roiFormSchema)
  });

  const { mutateAsync: createROI } = useMutation({
    mutationFn: (values: CreateROIPayload) => releaseOfInformationRepository.create(values)
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const activeParams = {
    page: orgDefaultSearchParams.page,
    search: searchParams.get('search') ?? orgDefaultSearchParams.search
  };
  const { page, search } = activeParams;

  const { data: orgData, isLoading: isLoadingOrgs } = useOrganizations({
    params: {
      page,
      ...(search && { search })
    },
    enabled: !!searchParams.get('search')
  });

  const roiForValue = watch('roiFor');
  const personOptionValue = watch('person');

  useEffect(() => {
    const searchOrgOptions = orgData?.data?.results?.map(formatOrganizationToSelectOption) || [];
    const values = getValues();

    const orgSelected = values.organization;
    if (
      orgSelected &&
      orgSelected.value !== emptySelectOption.value &&
      !searchOrgOptions.find((opt) => opt.value === orgSelected.value)
    ) {
      searchOrgOptions.push(orgSelected);
    }

    setOrganizationOptions(searchOrgOptions);
  }, [orgData, setOrganizationOptions, getValues]);

  useEffect(() => {
    if (person_relationships && person_relationships.length > 0) {
      const personRelationshipOptions = person_relationships.map(
        formatPersonRelationshipToSelectOption
      );
      setPersonOptions(personRelationshipOptions);
    }
  }, [person_relationships, setPersonOptions]);

  useEffect(() => {
    if (roiForValue === ReleaseOfInformationRecipientType.PERSON) {
      resetField('person');
    } else if (roiForValue === ReleaseOfInformationRecipientType.ORGANIZATION) {
      resetField('organization');
    }
  }, [roiForValue, resetField]);

  // ROIs are generated as forms in Healthie, so the member needs an account there before
  // we can create one. Prompt for the account instead of showing an unusable form.
  if (!person.healthie_id) {
    return (
      <Card>
        <Heading level={1}>New Release of Information</Heading>
        <Alert level="warn" dismissable={false}>
          This member doesn&apos;t have a Healthie account yet. Releases of Information are
          generated in Healthie, so one must be created before you can add an ROI.
        </Alert>
        <div className="mt-4">
          <CreateHealthieAccountChannel personId={person.id}>
            <CreateHealthieAccountButton person={person} />
          </CreateHealthieAccountChannel>
        </div>
      </Card>
    );
  }

  const onSubmit = async (values: ROIFormValues) => {
    try {
      if (!person.id || !values.roiFor) {
        return;
      }

      let object_id: string | undefined;

      if (values.roiFor === ReleaseOfInformationRecipientType.ORGANIZATION) {
        object_id = values.organization?.value;
      } else if (values.roiFor === ReleaseOfInformationRecipientType.PERSON) {
        object_id = values.person?.value;
      }

      if (!object_id) {
        return;
      }

      const createRoiPayload: CreateROIPayload = {
        person_id: person.id,
        recipient_type: values.roiFor,
        object_id
      };
      await createROI(createRoiPayload);
      addMessage({ level: 'success', text: 'Successfully created ROI' });
      onSuccess && onSuccess();
    } catch (error) {
      addMessage(mapAxiosErrorMessages(error as AxiosError));
    }
  };

  const handleOrganizationLookup = async (search: string) => {
    if (search) {
      setSearchParams((currentParams) => {
        currentParams.set('search', search);
        return currentParams;
      });
    }
  };

  return (
    <Card>
      <Heading level={1}>New Release of Information</Heading>
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputGroup>
          <Controller
            control={control}
            name="roiFor"
            render={({ field }) => (
              <RadioGroup
                id="roiFor"
                label="Who's the ROI for?"
                options={ROI_FOR_OPTIONS}
                selectedValue={field.value || ''}
                onChange={field.onChange}
              />
            )}
          />
          <FieldError message={errors.roiFor?.message} />
        </InputGroup>

        {roiForValue === ReleaseOfInformationRecipientType.ORGANIZATION && (
          <InputGroup>
            <Controller
              control={control}
              name="organization"
              render={({ field: { onChange, value } }) => (
                <AutocompleteSingleSelect
                  clearable={true}
                  label={
                    <>Search for the Organization {isLoadingOrgs && <Spinner className="ml-2" />}</>
                  }
                  onChange={onChange}
                  onSearch={handleOrganizationLookup}
                  options={organizationOptions}
                  missingOptionsText="Start typing or try another search query to see results..."
                  placeholder="ex. Octavius Industries"
                  selectedOption={
                    organizationOptions.find((option) => option.value === value?.value) ||
                    emptySelectOption
                  }
                />
              )}
            />
            <FieldError message={errors.organization?.value?.message} />
          </InputGroup>
        )}

        {roiForValue === ReleaseOfInformationRecipientType.PERSON && (
          <InputGroup>
            <div className="mb-2">
              <Alert level="info" dismissable={false}>
                If the person relationship does not appear in the list, you can add it to their
                profile under{' '}
                <a
                  href={`/people/${person.id}#care-team`}
                  className="text-blue-600"
                  target="_blank"
                  rel="noreferrer">
                  Member Care
                </a>
              </Alert>
            </div>
            <Controller
              control={control}
              name="person"
              render={({ field: { onChange, value } }) => (
                <AutocompleteSingleSelect
                  clearable={true}
                  label="Select a Person"
                  onChange={onChange}
                  options={personOptions}
                  placeholder="ex. John Doe (Parent)"
                  selectedOption={
                    personOptions.find((option) => option.value === value?.value) ||
                    emptySelectOption
                  }
                />
              )}
            />
            {personOptionValue?.value && (
              <a
                href={`/people/${personOptionValue.value}`}
                className="text-blue-600 text-sm"
                target="_blank"
                rel="noreferrer">
                View Profile
              </a>
            )}

            <FieldError message={errors.person?.value?.message} />
          </InputGroup>
        )}

        <div className="flex gap-2">
          <Button type="submit" loadingWithSpinner loading={isSubmitting}>
            Create
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default ROIForm;
